import React, { useState, useEffect } from 'react';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ColumnMappingPanel from './components/ColumnMappingPanel';
import DataPreviewPanel from './components/DataPreviewPanel';
import NormalizationSettings from './components/NormalizationSettings';
import FileSourceInfo from './components/FileSourceInfo';

const ColumnMappingInterface = () => {
  const [files, setFiles] = useState([]);
  const [sourceColumns, setSourceColumns] = useState([]);
  const [mappings, setMappings] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [normalizationSettings, setNormalizationSettings] = useState({
    currencyFormat: 'USD',
    numberFormat: 'US',
    dateFormat: 'AUTO',
    textCleaningRules: {
      trimWhitespace: true,
      removeExtraSpaces: true,
      standardizeCase: false,
      removeBrandDuplicates: false,
      standardizeUnits: false,
      removeSpecialChars: false
    },
    numericCleaningRules: {
      removeCurrencySymbols: true,
      standardizeDecimals: true,
      removeThousandsSeparators: true,
      handleNegativeNumbers: true
    }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  // Mock data initialization
  useEffect(() => {
    // Simulate loaded files from previous step
    const mockFiles = [
      {
        name: "supplier_a_prices.csv",
        size: 245760,
        rowCount: 1250,
        columnCount: 8,
        uploadedAt: new Date(Date.now() - 300000),
        status: 'processed',
        columns: ['Product Name', 'Brand', 'Part Number', 'Price USD', 'Stock Qty', 'Category', 'Supplier Code', 'Last Updated']
      },
      {
        name: "supplier_b_inventory.xlsx",
        size: 512000,
        rowCount: 890,
        columnCount: 10,
        uploadedAt: new Date(Date.now() - 180000),
        status: 'processed',
        columns: ['Item Description', 'Manufacturer', 'MPN', 'Unit Price', 'Available Stock', 'Product Category', 'EAN Code', 'Supplier', 'Currency', 'Updated Date']
      }
    ];

    const mockSourceColumns = [
      {
        name: 'Product Name',
        type: 'text',
        sample: 'Samsung Galaxy S24 Ultra 256GB',
        samples: ['Samsung Galaxy S24 Ultra 256GB', 'Apple iPhone 15 Pro Max', 'Dell XPS 13 Laptop'],
        nonNullCount: 1250,
        uniqueCount: 1180
      },
      {
        name: 'Brand',
        type: 'text',
        sample: 'Samsung',
        samples: ['Samsung', 'Apple', 'Dell', 'HP', 'Sony'],
        nonNullCount: 1250,
        uniqueCount: 45
      },
      {
        name: 'Part Number',
        type: 'text',
        sample: 'SM-S928U-256GB-BLK',
        samples: ['SM-S928U-256GB-BLK', 'IPHONE15PM-256-NAT', 'XPS13-9340-I7'],
        nonNullCount: 1180,
        uniqueCount: 1180
      },
      {
        name: 'Price USD',
        type: 'numeric',
        sample: '$1,299.99',
        samples: ['$1,299.99', '$1,199.00', '$899.50'],
        nonNullCount: 1250,
        uniqueCount: 890
      },
      {
        name: 'Stock Qty',
        type: 'numeric',
        sample: '25',
        samples: ['25', '0', '150', '8'],
        nonNullCount: 1200,
        uniqueCount: 180
      },
      {
        name: 'Category',
        type: 'text',
        sample: 'Smartphones',
        samples: ['Smartphones', 'Laptops', 'Tablets', 'Accessories'],
        nonNullCount: 1250,
        uniqueCount: 12
      },
      {
        name: 'Item Description',
        type: 'text',
        sample: 'Apple iPhone 15 Pro Max 512GB Natural Titanium',
        samples: ['Apple iPhone 15 Pro Max 512GB Natural Titanium', 'Dell XPS 13 9340 Intel i7', 'Sony WH-1000XM5 Headphones'],
        nonNullCount: 890,
        uniqueCount: 850
      },
      {
        name: 'EAN Code',
        type: 'text',
        sample: '194253715634',
        samples: ['194253715634', '885909715634', '123456789012'],
        nonNullCount: 780,
        uniqueCount: 780
      }
    ];

    const mockPreviewData = [
      {
        'Product Name': 'Samsung Galaxy S24 Ultra 256GB',
        'Brand': 'Samsung',
        'Part Number': 'SM-S928U-256GB-BLK',
        'Price USD': '$1,299.99',
        'Stock Qty': '25',
        'Category': 'Smartphones',
        'Item Description': 'Samsung Galaxy S24 Ultra 256GB Titanium Black',
        'EAN Code': '194253715634'
      },
      {
        'Product Name': 'Apple iPhone 15 Pro Max',
        'Brand': 'Apple',
        'Part Number': 'IPHONE15PM-256-NAT',
        'Price USD': '$1,199.00',
        'Stock Qty': '12',
        'Category': 'Smartphones',
        'Item Description': 'Apple iPhone 15 Pro Max 256GB Natural Titanium',
        'EAN Code': '194253715635'
      },
      {
        'Product Name': 'Dell XPS 13 Laptop',
        'Brand': 'Dell',
        'Part Number': 'XPS13-9340-I7',
        'Price USD': '$1,499.99',
        'Stock Qty': '8',
        'Category': 'Laptops',
        'Item Description': 'Dell XPS 13 9340 Intel i7 16GB RAM 512GB SSD',
        'EAN Code': '885909715634'
      },
      {
        'Product Name': 'Sony WH-1000XM5 Headphones',
        'Brand': 'Sony',
        'Part Number': 'WH1000XM5-B',
        'Price USD': '$399.99',
        'Stock Qty': '45',
        'Category': 'Audio',
        'Item Description': 'Sony WH-1000XM5 Wireless Noise Canceling Headphones Black',
        'EAN Code': '027242920156'
      },
      {
        'Product Name': 'HP Pavilion 15 Laptop',
        'Brand': 'HP',
        'Part Number': 'PAV15-EG2000',
        'Price USD': '$749.99',
        'Stock Qty': '0',
        'Category': 'Laptops',
        'Item Description': 'HP Pavilion 15 Intel i5 8GB RAM 256GB SSD',
        'EAN Code': '195161000123'
      }
    ];

    // Auto-detected mappings with confidence scores
    const mockMappings = {
      'Product Name': { field: 'product_name', confidence: 0.95 },
      'Brand': { field: 'brand', confidence: 0.98 },
      'Part Number': { field: 'mpn', confidence: 0.92 },
      'Price USD': { field: 'price', confidence: 0.96 },
      'Stock Qty': { field: 'stock', confidence: 0.94 },
      'Category': { field: 'category', confidence: 0.89 },
      'Item Description': { field: 'product_name', confidence: 0.85 },
      'EAN Code': { field: 'ean', confidence: 0.97 }
    };

    setFiles(mockFiles);
    setSourceColumns(mockSourceColumns);
    setMappings(mockMappings);
    setPreviewData(mockPreviewData);
  }, []);

  const handleMappingChange = (columnName, fieldValue) => {
    setMappings(prev => ({
      ...prev,
      [columnName]: {
        ...prev?.[columnName],
        field: fieldValue,
        confidence: prev?.[columnName]?.confidence || null
      }
    }));
  };

  const handleAutoMap = () => {
    setIsProcessing(true);
    
    // Simulate auto-mapping process
    setTimeout(() => {
      const autoMappings = {};
      sourceColumns?.forEach(column => {
        const columnName = column?.name?.toLowerCase();
        
        if (columnName?.includes('product') || columnName?.includes('item') || columnName?.includes('name')) {
          autoMappings[column.name] = { field: 'product_name', confidence: 0.9 };
        } else if (columnName?.includes('brand') || columnName?.includes('manufacturer')) {
          autoMappings[column.name] = { field: 'brand', confidence: 0.95 };
        } else if (columnName?.includes('mpn') || columnName?.includes('part')) {
          autoMappings[column.name] = { field: 'mpn', confidence: 0.92 };
        } else if (columnName?.includes('price') || columnName?.includes('cost')) {
          autoMappings[column.name] = { field: 'price', confidence: 0.94 };
        } else if (columnName?.includes('stock') || columnName?.includes('qty') || columnName?.includes('quantity')) {
          autoMappings[column.name] = { field: 'stock', confidence: 0.88 };
        } else if (columnName?.includes('ean') || columnName?.includes('upc') || columnName?.includes('barcode')) {
          autoMappings[column.name] = { field: 'ean', confidence: 0.96 };
        } else if (columnName?.includes('sku')) {
          autoMappings[column.name] = { field: 'sku', confidence: 0.91 };
        } else if (columnName?.includes('category')) {
          autoMappings[column.name] = { field: 'category', confidence: 0.87 };
        }
      });
      
      setMappings(autoMappings);
      setIsProcessing(false);
    }, 1500);
  };

  const handleResetMappings = () => {
    setMappings({});
  };

  const handleRemoveFile = (fileIndex) => {
    const newFiles = files?.filter((_, index) => index !== fileIndex);
    setFiles(newFiles);
  };

  const handleAddMoreFiles = () => {
    window.location.href = '/file-upload-dashboard';
  };

  const handleSaveTemplate = () => {
    // Simulate saving template
    console.log('Saving mapping template...', { mappings, normalizationSettings });
  };

  const handleLoadTemplate = () => {
    // Simulate loading template
    console.log('Loading mapping template...');
  };

  const handleContinueToProcessing = () => {
    const requiredFields = ['product_name', 'price'];
    const mappedFields = Object.values(mappings)?.map(m => m?.field);
    const hasRequiredFields = requiredFields?.every(field => mappedFields?.includes(field));
    
    if (!hasRequiredFields) {
      alert('Please map required fields: Product Name and Price');
      return;
    }
    
    window.location.href = '/processing-status-monitor';
  };

  const getValidationStatus = () => {
    const requiredFields = ['product_name', 'price'];
    const mappedFields = Object.values(mappings)?.map(m => m?.field);
    return requiredFields?.every(field => mappedFields?.includes(field));
  };

  const isValid = getValidationStatus();

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Progress Indicator */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <WorkflowProgressIndicator 
              currentStep={1}
              completedSteps={['upload']}
            />
          </div>
        </div>

        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Column Mapping Configuration</h1>
                <p className="text-muted-foreground mt-1">
                  Map your data columns to standard fields and configure normalization settings
                </p>
              </div>
              
              {/* Mobile Preview Toggle */}
              <div className="md:hidden">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMobilePreview(!showMobilePreview)}
                  iconName={showMobilePreview ? 'Eye' : 'EyeOff'}
                  iconPosition="left"
                >
                  {showMobilePreview ? 'Hide' : 'Show'} Preview
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* File Source Info */}
          <div className="mb-8">
            <FileSourceInfo
              files={files}
              onRemoveFile={handleRemoveFile}
              onAddMoreFiles={handleAddMoreFiles}
            />
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:grid md:grid-cols-12 gap-8">
            {/* Left Panel - Column Mapping */}
            <div className="col-span-5">
              <div className="space-y-6">
                <ColumnMappingPanel
                  sourceColumns={sourceColumns}
                  mappings={mappings}
                  onMappingChange={handleMappingChange}
                  onAutoMap={handleAutoMap}
                  onResetMappings={handleResetMappings}
                />
                
                <NormalizationSettings
                  settings={normalizationSettings}
                  onSettingsChange={setNormalizationSettings}
                  onSaveTemplate={handleSaveTemplate}
                  onLoadTemplate={handleLoadTemplate}
                />
              </div>
            </div>

            {/* Right Panel - Data Preview */}
            <div className="col-span-7">
              <DataPreviewPanel
                previewData={previewData}
                mappings={mappings}
                sourceColumns={sourceColumns}
              />
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-6">
            <ColumnMappingPanel
              sourceColumns={sourceColumns}
              mappings={mappings}
              onMappingChange={handleMappingChange}
              onAutoMap={handleAutoMap}
              onResetMappings={handleResetMappings}
            />

            {showMobilePreview && (
              <DataPreviewPanel
                previewData={previewData}
                mappings={mappings}
                sourceColumns={sourceColumns}
              />
            )}

            <NormalizationSettings
              settings={normalizationSettings}
              onSettingsChange={setNormalizationSettings}
              onSaveTemplate={handleSaveTemplate}
              onLoadTemplate={handleLoadTemplate}
            />
          </div>

          {/* Action Bar */}
          <div className="mt-8 bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={isValid ? 'CheckCircle' : 'AlertCircle'} 
                    size={20} 
                    className={isValid ? 'text-success' : 'text-warning'}
                  />
                  <span className={`text-sm font-medium ${isValid ? 'text-success' : 'text-warning'}`}>
                    {isValid ? 'Configuration Complete' : 'Missing Required Fields'}
                  </span>
                </div>
                
                {!isValid && (
                  <span className="text-sm text-muted-foreground">
                    Map Product Name and Price to continue
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/file-upload-dashboard'}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  Back to Upload
                </Button>
                
                <Button
                  variant="default"
                  onClick={handleContinueToProcessing}
                  disabled={!isValid || isProcessing}
                  loading={isProcessing}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  Continue to Processing
                </Button>
              </div>
            </div>

            {/* Progress Summary */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Icon name="FileText" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{files?.length}</span> files loaded
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Columns" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">
                      {Object.values(mappings)?.filter(m => m?.field && m?.field !== 'ignore')?.length}
                    </span> columns mapped
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Database" size={16} className="text-muted-foreground" />
                  <span className="text-muted-foreground">
                    <span className="font-medium text-foreground">{previewData?.length}</span> rows ready
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ColumnMappingInterface;